<template>
  <v-container>
    <v-row>
      <v-col cols="12" class="primary--text">
        <h1
          v-if="isNew"
          mt-10
        >
          <v-icon left color="primary">
            {{ $appConfig.api.icon }}
          </v-icon> API
        </h1>
      </v-col>
      <v-col cols="12">
        <!-- form START -->
        <v-form>
          <!-- name field START -->
          <v-text-field
            v-model="form.name"
            v-validate="'required|min:3|alpha_dash'"
            :disabled="!isNew"
            label="Name"
            :error-messages="errors.collect('name')"
            data-vv-name="name"
          />
          <!-- name field END -->

          <!-- description field START -->
          <v-textarea
            v-model="form.description"
            v-validate=""
            label="Description"
            :error-messages="errors.collect('description')"
            data-vv-name="description"
            outlined
            rows="2"
          />
          <!-- description field END -->

          <!-- labels START -->
          <hmd-label
            :curr-labels="currLabels"
            :items="isNew ? labels : labelsProp"
            @labels="updateLabels"
          />
          <!-- labels END -->

          <h1 class="title mt-4 display-1">
            Config
          </h1>
          <!-- upstream URL field START -->
          <v-text-field
            v-model="form.upstream_url"
            v-validate="'required|min:3|url'"
            label="Upstream URL"
            :error-messages="errors.collect('upstream_url')"
            data-vv-name="upstream_url"
            hint="The base target URL that points to your API server, this URL will be used for proxying requests. For example, ''https://himydata.com."
            required
          />
          <!-- upstream URL field END -->
          <v-row>
            <!-- request host field START -->
            <v-col
              cols="12"
              sm="6"
              md="6"
            >
              <v-text-field
                v-model="form.request_host"
                v-validate="'required|min:3'"
                label="Request Host"
                :error-messages="errors.collect('host')"
                data-vv-delay="1000"
                data-vv-name="host"
                required
              />
            </v-col>
            <!-- request host field END -->

            <!-- request path field START -->
            <v-col
              cols="12"
              sm="6"
              md="6"
            >
              <v-text-field
                v-model="form.request_path"
                v-validate="'required|min:3'"
                label="Request Path"
                :error-messages="errors.collect('request_path')"
                data-vv-delay="1000"
                data-vv-name="request_path"
                required
              />
            </v-col>
            <!-- request path field END -->

            <!-- protocol field START -->
            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                v-model="form.protocol"
                label="Protocol"
                :items="form.protocolItems"
                hint="Define supported protocol"
              />
            </v-col>
            <!-- protocol field END -->

            <!-- methods field START -->
            <v-col
              cols="12"
              sm="6"
            >
              <v-select
                v-model="form.methods"
                label="Methods"
                :items="form.methodsItems"
                multiple
                hint="Define supported methods"
              />
            </v-col>
            <!-- methods field END -->
          </v-row>

          <!-- privacy switch START -->
          <v-row align="center">
            <v-col
              class="mt-4"
              cols="2"
            >
              Privacy
            </v-col>
            <v-col
              class="mt-4"
              cols="1"
            >
              <v-switch
                v-model="form.isPrivate"
                color="primary"
              />
            </v-col>
            <v-col
              class="mt-4"
              cols="4"
            >
              <v-icon
                large
                :color="form.isPrivate ? 'primary' : 'grey'"
              >
                {{ form.isPrivate ? 'mdi-lock' : 'mdi-earth' }}
              </v-icon> {{ form.isPrivate ? 'Private mode' : 'Public mode' }}
            </v-col>
          </v-row>
          <!-- privacy switch END -->

          <!-- advanced config START -->
          <v-expansion-panels>
            <v-expansion-panel>
              <v-expansion-panel-header class="grey lighten-5">Advanced config</v-expansion-panel-header>
              <v-expansion-panel-content >
                <v-card>
                <v-row
                  class="pa-3"
                >
                  <v-col cols="6">
                    <v-text-field
                      v-model="form.timeout"
                      v-validate="'required|digits:5|max_value:60000'"
                      label="Timeout (ms)"
                      :error-messages="errors.collect('timeout')"
                      data-vv-name="timeout"
                      required
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-text-field
                      v-model="form.retries"
                      v-validate="'required|digits:1|max_value:5'"
                      label="Nb retries"
                      :error-messages="errors.collect('retries')"
                      data-vv-name="retries"
                      required
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-checkbox
                      v-model="form.preserve_host"
                      label="Preserve Host"
                    />
                  </v-col>
                  <v-col cols="6">
                    <v-checkbox
                      v-model="form.http_if_terminated"
                      label="http_if_terminated"
                    />
                  </v-col>
                </v-row>
              </v-card>
              </v-expansion-panel-content>
            </v-expansion-panel>
          </v-expansion-panels>
          <!-- advanced config END -->

          <!-- edit/create buttons START -->
          <v-row align="center">
            <v-col
              cols="12"
              class="text-center"
            >
              <v-btn
                :loading="creationLoadingButton"
                :disabled="creationLoadingButton"
                color="primary"
                block
                large
                @click="submit"
              >
                <v-icon
                  v-if="!isNew"
                  left
                >
                  mdi-pencil
                </v-icon>
                <v-icon
                  v-else
                  left
                >
                  mdi-plus
                </v-icon>
                {{ isNew ? "Create api" : "Update api" }}
                <span slot="loader">Creating API...</span>
              </v-btn>
            </v-col>
          </v-row>
          <!-- edit/create buttons END -->
          <!-- delete button START -->
          <v-col
            cols="12"
            class="text-center"
          >
            <v-dialog
              v-if="!isNew"
              v-model="deleteDialog"
              persistent
              max-width="400"
            >
              <template v-slot:activator="{ on }">
                <v-btn
                  outlined
                  color="error"
                  large
                  block
                  v-on="on"
                >
                  <v-icon left>
                    mdi-delete-outline
                  </v-icon>
                  Delete API
                </v-btn>
              </template>
              <v-card>
                <v-card-title class="headline">
                  Delete this API
                </v-card-title>
                <v-card-text>Are you sure you want to delete this API?</v-card-text>
                <v-card-actions>
                  <v-spacer />
                  <v-btn
                    color=""
                    text
                    @click.native="deleteDialog = false"
                  >
                    Cancel
                  </v-btn>
                  <v-btn
                    color="primary"
                    text
                    @click.native="deleteApi()"
                  >
                    Delete
                  </v-btn>
                </v-card-actions>
              </v-card>
            </v-dialog>
          </v-col>
          <!-- delete button END -->
        </v-form>
        <!-- form END -->
      </v-col>
    </v-row>
  </v-container>
</template>

<script>
  import ApiService from './services/ApiService'
  import HmdLabel from '../../../UIComponents/HmdLabel.vue'
  import HmdService from '../../../UIComponents/services/HmdService'

  export default {
    components: {
      HmdLabel,
    },
    $_veeValidate: {
      validator: 'new',
    },
    props: {
      isNew: {
        type: Boolean,
        default: function () {
          return true
        },
      },
      api: {
        type: Object,
        default: function () {
          return {}
        },
      },
      labelsProp: {
        type: Array,
        default: function () {
          return []
        },
      },
    },
    data: function () {
      return {
        form: {
          items: ['tous', 'private'],
          name: '',
          upstream_url: '',
          description: '',
          labels: [],
          isPrivate: true,
          request_host: '',
          request_path: '',
          uris: '',
          strip_url: '',
          timeout: 60000,
          retries: 3,
          preserve_host: false,
          http_if_terminated: false,
          protocol: 'HTTPS',
          protocolItems: ['HTTP', 'HTTPS'],
          methods: ['GET'],
          methodsItems: ['GET', 'POST', 'PUT', 'OPTION', 'DELETE'],

        },

        signinDictionary: {
          attributes: {
            email: 'E-mail Address',
            // custom attributes
          },
          custom: {
            password: {
              required: () => 'Name can not be empty',
              max: 'The name field may not be greater than 10 characters',
              // custom messages
            },
          },
        },
        notif: {
          snackbar: false,
          color: 'error',
          mode: '',
          timeout: 3000,
          text: '',
        },
        deleteDialog: false,
        currLabels: [],
        creationLoadingButton: false,
        labels: [],
      }
    },
    mounted: function () {
      this.$validator.localize('fr', this.signinDictionary)
      this.form.name = this.api.name
      this.form.description = this.api.description
      this.form.labels = this.api.labels
      this.form.upstream_url = this.api.upstream_url
      this.form.protocol = this.api.https_only
      if (typeof this.api.methods === 'undefined') {
        this.form.methods = ['GET']
      }
      this.form.request_host = this.api.request_host
      this.form.request_path = this.api.request_path
      this.form.preserve_host = this.api.preserve_host
      this.form.strip_request_path = this.api.strip_request_path
      this.form.http_if_terminated = this.api.http_if_terminated
      if (typeof this.api.is_private !== 'undefined') {
        this.form.isPrivate = this.api.is_private
      }
      if (this.isNew) {
        this.form.retries = 3
        this.form.timeout = 60000
        HmdService.label_list()
          .then(result => {
            this.labels = result.data
          })
      } else {
        this.form.retries = this.api.retries
        this.form.timeout = this.api.upstream_connect_timeout
      }
      var i
      for (i in this.api.labels) {
        this.currLabels.push(this.api.labels[i].id)
      }
    },
    methods: {
      remove_label (item) {
        this.labels.splice(this.labels.indexOf(item), 1)
        this.labels = [...this.labels]
      },
      updateLabels (labels) {
        this.form.labels = labels
      },
      submit () {
        this.$validator.validateAll().then((result) => {
          if (result) {
            // eslint-disable-next-line
            let https_only = false
            // eslint-disable-next-line
            if (this.form.protocol === 'HTTPS') { https_only = true }
            var api = {
              name: this.form.name,
              description: this.form.description,
              labels: this.form.labels,
              upstream_url: this.form.upstream_url,
              methods: this.form.methods,
              request_host: this.form.request_host,
              request_path: this.form.request_path,
              preserve_host: this.form.preserve_host,
              strip_request_path: this.form.strip_request_path,
              retries: this.form.retries,
              https_only: https_only,
              http_if_terminated: this.form.http_if_terminated,
              upstream_connect_timeout: this.form.timeout,
              is_private: this.form.isPrivate,
            }
            if (this.isNew) {
              let t = this

              // Uncomment the following lines if you want to check first if the API name is already taken.
              // Note : The checking is done during the POST request too, but it could be useful for displaying extra-steps (e.g. displaying dialog for creation as in DatasetCreate)
              // ApiService.list(this,  0).then(function(response){
              //   let nameUsed = false

              //   response.data.results.some(elem => {
              //     if (elem.name === t.form.name) {
              //       nameUsed = true
              //       t.errors.add("name", "API Reference with this name already exists.")
              //       t.$store.commit('push', {msg: "API " + elem.name + " already exists in your database", color: 'error'});
              //       return true
              //     }
              //   })

              // if (!nameUsed) {
              t.creationLoadingButton = true
              ApiService.create(api, t).then(function (response) {
                t.$emit('create', response.data)
              }).finally(() => {
                t.creationLoadingButton = false
              })
              //   }
              // })
            } else {
              ApiService.update(api, this)
            }
          }
        })
      },
      deleteApi () {
        ApiService.delete(this.api.name, this).then(() => {
          this.$emit('delete')
        })
      },
    },
  }
</script>
